import {
  Box,
  CircularProgress,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTable, useSortBy } from 'react-table'
import AdxEmptyState from 'components/reusable/emptyState/AdxEmptyState'
import RecruitmentPagination from 'module/recruitment/reusable/RecruitmentPagination'
import AddToCartModal from './AddToCartModal'
import CalendarDetailsModal from '../../recruitment-talent-calendar/components/CalendarDetailsModal'
import { useQuery, useQueryClient } from 'react-query'
import { getInterviewSlot } from 'services/api/modules/recruitment/external-talent'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import { PersonOff } from '@mui/icons-material'

const CandidateList = ({
  data,
  column,
  nav,
  onClick,
  sx,
  sxHeader,
  sxCell,
  sxNav,
  stickyHeader,
  allColumnSorted = true,
  lastPage,
  handlePageChange,
  allAplicantIsLoading,
  searchIsLoading,
  title = '',
  error,
  count,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  searchItem,
  setIsChoosenName,
  setIsChoosenId,

  ...rest
}) => {
  //for interview details column only
  const [openInterviewModal, setInterviewModal] = useState(false)
  const [interviewId, setInterviewId] = useState()
  const handleToggleInterviewModal = () => {
    setInterviewModal((prev) => !prev)
  }

  // const queryClient = useQueryClient()
  const { data: interviewData, isLoading: interviewDataIsLoading } = useQuery(
    ['getInterviewSlot', interviewId],
    async () => {
      if (interviewId) {
        const { data } = await getInterviewSlot(interviewId)
        return data
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const endDateObject = new Date(interviewData?.end_date)

  const emptyStateMessage = `No ${title} candidate available`
  const tableHeader = useMemo(() => column, [column])
  const tableInstance = useTable(
    {
      columns: tableHeader,
      data: data,
      sortTypes: {
        alphanumeric: (prev, curr, columnId) => {
          // if the data type is a number e
          if (Number.isInteger(prev?.original[columnId])) {
            if (prev?.original[columnId] > curr?.original[columnId]) {
              return 1
            } else if (prev?.original[columnId] < curr?.original[columnId]) {
              return -1
            } else {
              return 0
            }

            // if the data is and true or false data type
          } else if (typeof prev.original[columnId] === 'boolean') {
            if (prev.original[columnId] === true && curr.original[columnId] === false) {
              return 1
            } else if (prev.original[columnId] === false && curr.original[columnId] === true) {
              return -1
            } else {
              return 0
            }

            // if the data type is a string
          } else {
            if (prev?.original[columnId]?.toLowerCase() > curr?.original[columnId]?.toLowerCase()) {
              return 1
            } else if (
              prev?.original[columnId]?.toLowerCase() < curr?.original[columnId]?.toLowerCase()
            ) {
              return -1
            } else {
              return 0
            }
          }
        },
      },
    },
    useSortBy
  )
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance

  if (allAplicantIsLoading || searchIsLoading) {
    return (
      <TableContainer
        sx={{
          backgroundColor: 'white.main',
          '& td.MuiTableCell-root': {
            padding: '0px',
          },
          ...sx,
        }}
        {...rest}
      >
        <Table stickyHeader={stickyHeader} {...getTableProps}>
          <TableHead
            sx={{
              backgroundColor: 'gray-background.main',
              // position:"absolute"
            }}
          >
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <TableCell
                    align="center"
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'light-gray-text.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: index === 0 ? 'left' : 'center',
                        ...sxHeader,
                      }}
                    >
                      {column.render('Header')}
                      {allColumnSorted &&
                        column.Header !== 'Name' &&
                        column.Header !== 'Job fit' &&
                        column.Header !== 'Attachment' && (
                          <>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon sx={{ fontSize: '32px', color: 'alternate.main' }}>
                                  arrow_drop_up
                                </Icon>
                              ) : (
                                <Icon sx={{ fontSize: '32px', color: 'alternate.main' }}>
                                  arrow_drop_down
                                </Icon>
                              )
                            ) : (
                              <Icon
                                sx={{
                                  fontSize: '32px',
                                  color: 'gray-inactive-button.main',
                                }}
                              >
                                arrow_drop_down
                              </Icon>
                            )}
                          </>
                        )}
                    </Typography>
                  </TableCell>
                ))}
                {nav && (
                  <TableCell>
                    <Typography></Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableHead>
          {Array.from({ length: 4 }).map((_, rowIndex) => (
            <TableBody key={rowIndex} {...getTableBodyProps()}>
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, index) =>
                    index === 0 ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        columnGap={2}
                        padding={2}
                        sx={{
                          borderBottom: '1px solid',
                          borderBottomColor: 'gray-line.main',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          backgroundColor: 'default-background.main',
                        }}
                      >
                        <Box display="flex" alignItems="center" columnGap={2}>
                          <Box display="flex" justifyContent="center" alignItems="center">
                            <Skeleton variant="circular" width={45} height={45} />
                          </Box>
                        </Box>

                        <Box
                          display="flex"
                          flexDirection={{ xs: 'column', lg: 'row' }}
                          rowGap={2}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Box display="flex" columnGap={1} alignItems="start" flexWrap="wrap">
                              <Typography
                                variant="body1"
                                color="dark-text.main"
                                marginBottom={0.5}
                                sx={{
                                  textTransform: 'capitalize',
                                  // display: "-webkit-box",
                                  // WebkitLineClamp: 2,
                                  // WebkitBoxOrient: "vertical",
                                  maxWidth: 250,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <Skeleton variant="text" width={550} />
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" columnGap={3}>
                              <Box display="flex" alignItems="center" columnGap={1}>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="light-gray-text.main"
                                >
                                  <Skeleton variant="text" width={300} />
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <TableCell
                        align="center"
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                      >
                        <Box padding={2}>
                          <Skeleton variant="rounded" width={'80%'} />
                        </Box>
                      </TableCell>
                    )
                  )}
                  {nav && (
                    <TableCell>
                      <Skeleton variant="rounded" width={'80%'} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          ))}
        </Table>
      </TableContainer>
    )
  } else if (error !== null || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <AdxEmptyStateV2 icon={<PersonOff />} title={emptyStateMessage} />
      </Box>
    )
  } else {
    return (
      <Box>
        <TableContainer
          sx={{
            backgroundColor: 'white.main',

            '& th.MuiTableCell-root': {},
            '& td.MuiTableCell-root': {
              padding: '0px',
            },
            ...sx,
          }}
          {...rest}
        >
          <Table stickyHeader={stickyHeader} {...getTableProps}>
            <TableHead
              sx={{
                backgroundColor: 'gray-background.main',
                // position:"absolute"
              }}
            >
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, index) => (
                    <TableCell
                      align="center"
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'light-gray-text.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: column.Header === 'Job fit' ? 'center' : 'start',
                          ...sxHeader,
                        }}
                      >
                        {column.render('Header')}
                        {allColumnSorted &&
                          column.Header !== 'Name' &&
                          column.Header !== 'Job fit' &&
                          column.Header !== 'Attachment' && (
                            <>
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <Icon sx={{ fontSize: '32px', color: 'alternate.main' }}>
                                    arrow_drop_up
                                  </Icon>
                                ) : (
                                  <Icon sx={{ fontSize: '32px', color: 'alternate.main' }}>
                                    arrow_drop_down
                                  </Icon>
                                )
                              ) : (
                                <Icon
                                  sx={{
                                    fontSize: '32px',
                                    color: 'gray-inactive-button.main',
                                  }}
                                >
                                  arrow_drop_down
                                </Icon>
                              )}
                            </>
                          )}
                      </Typography>
                    </TableCell>
                  ))}
                  {nav && (
                    <TableCell>
                      <Typography></Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row)
                return (
                  <TableRow
                    sx={{
                      '&:hover': {
                        backgroundColor: 'default-background.main',
                      },
                    }}
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell, index) => {
                      return (
                        <>
                          <TableCell
                            align="center"
                            {...cell.getCellProps()}
                            // check if it is interview detail column, handletoggle
                            onClick={() => {
                              cell?.column?.Header === 'Interview detail'
                                ? handleToggleInterviewModal()
                                : onClick(row.original)
                              // to set which candidate is clicked
                              setIsChoosenName(row.original?.relations?.candidateable?.name)
                              setIsChoosenId(row.original.id)
                              // setInterviewModalData(cell?.value)
                              setInterviewId(cell?.value?.id)
                            }}
                            sx={{
                              cursor: 'pointer',
                              maxWidth: 500,
                            }}
                          >
                            <Typography
                              sx={{
                                textAlign: index === 0 && 'left',
                                p: 2,
                                //only for interview details, the wording will be underline on hover
                                ...(cell?.column?.Header === 'Interview detail'
                                  ? {
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline' },
                                    }
                                  : {}),
                                ...sxCell,
                              }}
                            >
                              {cell.render('Cell')}
                            </Typography>
                          </TableCell>
                        </>
                      )
                    })}
                    <TableCell
                      align="center"
                      sx={{
                        borderTop: '1px solid',
                        borderColor: 'gray-line.main',
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: 'alternate.main',
                          cursor: 'pointer',
                          padding: '18px 30px',
                          ...sxNav,
                          textDecoration: 'underline',
                        }}
                        onClick={() => {
                          if (
                            row?.original?.relations?.candidateable?.is_applicant_in_cart ||
                            row?.original?.relations?.candidateable?.pricing?.is_unlocked
                          ) {
                            onClick(row.original)
                            // to set which candidate is clicked
                            setIsChoosenName(
                              row.original?.relations?.candidateable?.relations?.applicant?.name
                            )
                            setIsChoosenId(row.original?.id)
                          }
                        }}
                      >
                        {row?.original?.relations?.candidateable?.is_applicant_in_cart ||
                        row?.original?.relations?.candidateable?.pricing?.is_unlocked ? (
                          nav
                        ) : (
                          <AddToCartModal id={row?.original?.relations?.candidateable?.id} />
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {/*  show interview modal only for interview detail header */}
        <CalendarDetailsModal
          open={openInterviewModal}
          handleClose={handleToggleInterviewModal}
          data={interviewData}
          dateData={endDateObject}
          interviewSlotIsLoading={interviewDataIsLoading}
        />
        {searchItem?.length < 3 ? (
          <Box
            bgcolor="white.main"
            display="flex"
            justifyContent="flex-end"
            sx={{
              width: '100%',
              px: 2,
              py: 1,
            }}
          >
            <RecruitmentPagination
              count={count}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          </Box>
        ) : null}
      </Box>
    )
  }
}

export default CandidateList
